
import { Options, Vue } from "vue-class-component";
import store from "@/store";
import i18n from "@/i18n";
import { MenuRoles, screenAuthorityKeys } from "@/types";
import { checkUserByRole, isAdminOrSuperAdmin } from "@/utils";

type NavLink = {
  key: string;
  id: string;
  role: MenuRoles;
  to: string;
  icon: string;
  module: string;
  testId?: string;
};

type NavChild = {
  key: string;
  id: string;
  role: MenuRoles;
  to: string;
  module: string;
  // Bypasses the role check below - remove once ROLE_MENU_TRANSLATIONS is
  // actually granted to users via the backend/role-menu admin screen.
  alwaysVisible?: boolean;
};

const SETTINGS_MODULES = ["availability", "scale", "asset", "garanty", "screnConfiguration"];

const MENU_LABEL_FALLBACKS: Partial<Record<MenuRoles, string>> = {
  [MenuRoles.ROLE_MENU_DASHBOARD]: "main.menu.dashboard",
  [MenuRoles.ROLE_MENU_GLOBAL_SETTINGS]: "main.menu.globalSettings",
  [MenuRoles.ROLE_MENU_PLANNIG]: "main.menu.deliverySchedule",
  [MenuRoles.ROLE_MENU_TRANSLATIONS]: "main.menu.translation",
  [MenuRoles.ROLE_MENU_MAIL_CONFIG]: "main.menu.mailConfiguration",
  [MenuRoles.ROLE_MENU_IMPORT_EXPORT_PARAM]: "main.menu.importExportParam",
  [MenuRoles.ROLE_MENU_STATIC_TEMPLATE]: "main.menu.staticTemplate",
};

@Options({
  name: "app-sidebar",
  watch: {
    "$store.state.module"(newValue: string) {
      this.module = newValue;
      this.syncExpandedSections();
    },
  },
})
export default class Sidebar extends Vue {
  openConfiguration = false;
  openUserAdmin = false;
  module = "";
  MenuRoles = MenuRoles;
  checkUserByRole = checkUserByRole;

  mainLinks: NavLink[] = [
    { key: "home", id: "menuHome", role: MenuRoles.ROLE_MENU_HOME, to: "/home", icon: "home", module: "home" },
    { key: "demand", id: "menu_offer", role: MenuRoles.ROLE_MENU_OFFER, to: "/demand/list", icon: "assignment", module: "demand" },
   // { key: "dossier", id: "menu_order", role: MenuRoles.ROLE_MENU_ORDER, to: "/order", icon: "store", module: "dossier" },
    { key: "third", id: "menu_third", role: MenuRoles.ROLE_MENU_THIRD, to: "/third/list", icon: "group", module: "third", testId: "third_sideBar" },
    { key: "task", id: "menu_tasks", role: MenuRoles.ROLE_MENU_TASKS, to: "/task/list", icon: "task_alt", module: "task", testId: "action-menu-item" },
  ];

  settingsChildren: NavChild[] = [
    { key: "availability", id: "menu_availability", role: MenuRoles.ROLE_MENU_AVAILABILITY, to: "/availability/list", module: "availability" },
    { key: "scale", id: "menu_scale", role: MenuRoles.ROLE_MENU_SCALE, to: "/scale/list", module: "scale" },
    { key: "good", id: "menu_good", role: MenuRoles.ROLE_MENU_GOOD, to: "/good/list", module: "asset" },
    { key: "garanty", id: "menu_garanty", role: MenuRoles.ROLE_MENU_GARANTY, to: "/garanty/list", module: "garanty" },
    { key: "screnConfiguration", id: "menu_screnConfiguration", role: MenuRoles.ROLE_MENU_ENTITYCONFIGURATION, to: "/screnConfiguration/list", module: "screnConfiguration" },
  ];

  adminChildren: NavChild[] = [
    { key: "users", id: "menu_users", role: MenuRoles.ROLE_MENU_USERS, to: "/userAdmin/list", module: "userAdmin" },
    { key: "mailConfig", id: "menu_mail_config", role: MenuRoles.ROLE_MENU_MAIL_CONFIG, to: "/mailConfig/list", module: "mailConfig" },
    { key: "importExportParam", id: "menu_import_export_param", role: MenuRoles.ROLE_MENU_IMPORT_EXPORT_PARAM, to: "/importExportParam", module: "importExportParam" },
    { key: "staticTemplate", id: "menu_static_template", role: MenuRoles.ROLE_MENU_STATIC_TEMPLATE, to: "/staticTemplate/list", module: "staticTemplate" },
    { key: "screenAuthority", id: "menu_screen_authority", role: MenuRoles.ROLE_MENU_SCREEN_AUTHORITY, to: "/role-menu", module: "userAdmin" },
    { key: "superset", id: "menu_superset", role: MenuRoles.ROLE_MENU_SUPERSET, to: "/superset/list", module: "userAdmin" },
    { key: "dashboard", id: "menu_screen_dashboard", role: MenuRoles.ROLE_MENU_DASHBOARD, to: "/dashboard", module: "userAdmin" },
    { key: "globalSettings", id: "menu_screen_global_settings", role: MenuRoles.ROLE_MENU_GLOBAL_SETTINGS, to: "/global-settings/edit", module: "userAdmin" },
    { key: "translation", id: "menu_translation", role: MenuRoles.ROLE_MENU_TRANSLATIONS, to: "/translation/list", module: "userAdmin" },
  ];

  get visibleMainLinks(): NavLink[] {
    return this.mainLinks.filter((item) => checkUserByRole([item.role]));
  }

  get settingsSectionVisible(): boolean {
    return checkUserByRole([MenuRoles.ROLE_MENU_SETTINGS]);
  }

  get adminSectionVisible(): boolean {
    return checkUserByRole([MenuRoles.ROLE_MENU_ADMIN]);
  }

  get visibleSettingsChildren(): NavChild[] {
    return this.settingsChildren.filter((item) => checkUserByRole([item.role]));
  }

  get visibleAdminChildren(): NavChild[] {
    return this.adminChildren.filter((item) => {
      if (item.key === "mailConfig" || item.key === "importExportParam" || item.key === "staticTemplate") {
        return isAdminOrSuperAdmin();
      }
      return item.alwaysVisible || checkUserByRole([item.role]);
    });
  }

  created() {
    this.module = store.state.module || "";
    this.syncExpandedSections();
  }

  menuLabel(role: MenuRoles): string {
    const dynamicKey = `main.menu.${role}`;
    const dynamicLabel = i18n.global.t(dynamicKey);
    if (dynamicLabel !== dynamicKey) {
      return dynamicLabel;
    }
    const fallbackKey = MENU_LABEL_FALLBACKS[role] || screenAuthorityKeys[role];
    if (!fallbackKey) {
      return dynamicLabel;
    }
    const fallbackLabel = i18n.global.t(fallbackKey);
    return fallbackLabel !== fallbackKey ? fallbackLabel : dynamicLabel;
  }

  isModuleActive(moduleKey: string): boolean {
    return !!moduleKey && this.module === moduleKey;
  }

  labelClass(moduleKey: string, isSubItem = false): string {
    const active = this.isModuleActive(moduleKey);
    const base = isSubItem ? "app-sidebar__sublabel" : "app-sidebar__label";
    return active ? `${base} text-primary` : `${base} text-grey-8`;
  }

  iconColor(moduleKey: string): string {
    return this.isModuleActive(moduleKey) ? "primary" : "grey-7";
  }

  syncExpandedSections() {
    if (SETTINGS_MODULES.includes(this.module)) {
      this.openConfiguration = true;
    }
    if (this.module === "userAdmin" || this.module === "mailConfig" || this.module === "importExportParam" || this.module === "staticTemplate") {
      this.openUserAdmin = true;
    }
  }

  hide() {
    this.$emit("closeDrawer", "Close the drawer");
  }

  openSection() {
    this.openConfiguration = !this.openConfiguration;
  }

  openUserAdminSection() {
    this.openUserAdmin = !this.openUserAdmin;
  }
}
